/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=3%3A10
import type { PropsWithChildren } from 'react';
import React from 'react';
import type { Theme } from '@emotion/react';
import type { LinkProps, SxProps, Theme as MuiTheme } from '@mui/material';
import type { Interpolation } from '@emotion/serialize';
import type { ITheme } from '../../styles/theme';
import { theme as defaultTheme } from '../../styles/theme';
import Typography, { TypographyVariant } from '../Typography';
import { type IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { BREAKPOINT } from '../../utils/breakpoints';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';
import { BoxWithId } from '../BoxWithId/BoxWithId';
import { ButtonVariant, LinkButton } from '../Button';

interface IProps extends IPropsWithTestId {
  theme?: ITheme;
  muiTheme: MuiTheme;
  link: IPropsWithTestId & {
    title: string;
    to: string;
  };
  additionalStyles?: Interpolation<Theme>;
}

const Popout: React.FC<
PropsWithChildren<IProps & Omit<LinkProps, 'style' | 'children' | 'color'>>
> = ({
  children,
  theme = defaultTheme,
  muiTheme,
  dataElementId,
  link,
  additionalStyles = {},
}) => {
  const themeToken = useThemeToken();
  const { colors } = theme;

  const mainStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '96px',
    padding: '24px 48px',
    backgroundColor: colors.grayscale.white,
    boxShadow: '0px 2px 8px 0px #00000026',
    borderRadius: '10px',

    [muiTheme.breakpoints.only('mobile')]: {
      flexDirection: 'column',
      textAlign: 'center',
      justifyContent: 'flex-start',
      padding: '24px',
    },
  };

  const noteStyles = {
    [muiTheme.breakpoints.only('mobile')]: {
      marginBottom: '16px',
    },
  };

  const linkStyles = {
    minWidth: '100px',
    marginLeft: '24px',
    [muiTheme.breakpoints.only('mobile')]: {
      marginLeft: '0',
    },
  };

  return (
    <BoxWithId
      sx={mainStyles}
      css={additionalStyles}
      role="note"
      dataElementId={dataElementId}
    >
      <Typography
        variant={{
          [BREAKPOINT.DESKTOP]: TypographyVariant.LARGE_BODY_REGULAR,
          [BREAKPOINT.MOBILE]: TypographyVariant.MEDIUM_BODY_REGULAR,
        }}
        additionalStyles={noteStyles}
      >
        {children}
      </Typography>
      <LinkButton
        dataElementId={link.dataElementId}
        variant={ButtonVariant.TERTIARY}
        themeToken={themeToken}
        additionalStyles={linkStyles}
        to={link.to}
        inlineButton
      >
        {link.title}
      </LinkButton>
    </BoxWithId>
  );
};

export default Popout;
